import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import MapLibreCspWorker from 'maplibre-gl/dist/maplibre-gl-csp-worker?worker';
import mapLibreCspWorkerUrl from 'maplibre-gl/dist/maplibre-gl-csp-worker.js?url';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useNavigate } from 'react-router-dom';

// ─── Types ──────────────────────────────────────────────────────────
export interface RiskUOMapPoint {
  uo_id: string;
  uo_nome: string;
  cidade: string | null;
  lat: number | null;
  lng: number | null;
  municipality_code: string | null;
  score_risco_uo: number | null;
  nivel_calor: string;
  contract_id: string;
  contrato_nome: string;
  total_nao_conformidades: number;
  total_riscos_imediatos: number;
  progresso: number;
}

interface CityAggregate {
  cidade: string;
  lat: number;
  lng: number;
  municipality_code: string;
  uos: RiskUOMapPoint[];
  avg_score: number;
  max_nivel: string;
  total_contratos: number;
}

interface RiskUOMapProps {
  data: RiskUOMapPoint[];
  selectedUOId: string | null;
  onSelectUO: (uoId: string | null) => void;
}

// ─── Constants ──────────────────────────────────────────────────────
const CALOR_MAP_COLORS: Record<string, string> = {
  critico: '#B42318',
  alto: '#E5780B',
  moderado: '#C8A100',
  baixo: '#2E7D6B',
};

const NIVEL_ORDER: Record<string, number> = { critico: 3, alto: 2, moderado: 1, baixo: 0 };

let workerConfigured = false;
const ensureWorker = () => {
  if (workerConfigured) return;
  if (import.meta.env.PROD) {
    maplibregl.setWorkerUrl(mapLibreCspWorkerUrl);
  } else {
    (maplibregl as any).workerClass = MapLibreCspWorker;
  }
  workerConfigured = true;
};

// ─── Aggregate UOs by city ──────────────────────────────────────────
function aggregateByCidade(data: RiskUOMapPoint[]): CityAggregate[] {
  const map = new Map<string, CityAggregate>();

  for (const row of data) {
    if (!row.cidade || row.lat == null || row.lng == null) continue;
    const key = row.municipality_code || row.cidade;

    if (!map.has(key)) {
      map.set(key, {
        cidade: row.cidade,
        lat: row.lat,
        lng: row.lng,
        municipality_code: row.municipality_code || '',
        uos: [],
        avg_score: 0,
        max_nivel: 'baixo',
        total_contratos: 0,
      });
    }

    const agg = map.get(key)!;
    agg.uos.push(row);

    if (NIVEL_ORDER[row.nivel_calor] > NIVEL_ORDER[agg.max_nivel]) {
      agg.max_nivel = row.nivel_calor;
    }
  }

  // Compute aggregates
  for (const agg of map.values()) {
    const scores = agg.uos.map(u => u.score_risco_uo ?? 0);
    agg.avg_score = scores.length > 0
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10
      : 0;
    const contractIds = new Set(agg.uos.map(u => u.contract_id));
    agg.total_contratos = contractIds.size;
  }

  return Array.from(map.values());
}

function buildGeoJSON(cities: CityAggregate[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: cities.map(c => ({
      type: 'Feature' as const,
      properties: {
        cidade: c.cidade,
        municipality_code: c.municipality_code,
        avg_score: c.avg_score,
        max_nivel: c.max_nivel,
        color: CALOR_MAP_COLORS[c.max_nivel] || CALOR_MAP_COLORS.baixo,
        total_uos: c.uos.length,
        total_contratos: c.total_contratos,
        uo_ids: c.uos.map(u => u.uo_id).join(','),
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [c.lng, c.lat],
      },
    })),
  };
}

// ─── Component ──────────────────────────────────────────────────────
const RiskUOMap: React.FC<RiskUOMapProps> = ({ data, selectedUOId, onSelectUO }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const navigate = useNavigate();

  const cities = React.useMemo(() => aggregateByCidade(data), [data]);
  const geojson = React.useMemo(() => buildGeoJSON(cities), [cities]);

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    try {
      ensureWorker();
      const m = new maplibregl.Map({
        container: containerRef.current,
        style: {
          version: 8,
          sources: {
            'carto-tiles': {
              type: 'raster',
              tiles: ['https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png'],
              tileSize: 256,
              attribution: '&copy; CARTO',
            },
          },
          layers: [{ id: 'carto-base', type: 'raster', source: 'carto-tiles' }],
        },
        center: [-44.5, -20.2],
        zoom: 6.5,
        attributionControl: false,
      });

      m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

      m.on('load', () => {
        setMapReady(true);
      });

      mapRef.current = m;
    } catch (err) {
      console.error('[RiskUOMap] Failed to init map:', err);
    }

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // Add/update source + layer
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !mapReady) return;

    const sourceId = 'risk-cities';
    const layerId = 'risk-cities-circles';
    const labelLayerId = 'risk-cities-labels';

    if (m.getSource(sourceId)) {
      (m.getSource(sourceId) as maplibregl.GeoJSONSource).setData(geojson);
    } else {
      m.addSource(sourceId, { type: 'geojson', data: geojson });

      m.addLayer({
        id: layerId,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'total_uos'], 1, 10, 5, 18, 10, 26],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.85,
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
        },
      });

      m.addLayer({
        id: labelLayerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': ['get', 'total_uos'],
          'text-size': 11,
          'text-font': ['Open Sans Bold'],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Click handler
      m.on('click', layerId, (e) => {
        const f = e.features?.[0];
        if (!f || !f.properties) return;
        const p = f.properties;
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];

        const uoIds: string[] = (p.uo_ids as string).split(',');
        const cityUOs = data.filter(d => uoIds.includes(d.uo_id));

        // Select first UO for sync
        if (uoIds.length > 0) onSelectUO(uoIds[0]);

        // Build popup
        const uoList = cityUOs
          .map(u => `
            <div class="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
              <span class="text-xs font-medium truncate max-w-[160px]">${u.uo_nome}</span>
              <span class="text-xs font-bold ml-2" style="color: ${CALOR_MAP_COLORS[u.nivel_calor]}">${u.score_risco_uo ?? '—'}</span>
            </div>
          `).join('');

        const contractButtons = [...new Set(cityUOs.map(u => u.contract_id))].map(cid => {
          const c = cityUOs.find(u => u.contract_id === cid);
          return `<button data-contract-id="${cid}" class="risk-map-drill text-xs text-blue-600 hover:underline cursor-pointer bg-transparent border-0 p-0 text-left">${c?.contrato_nome || cid}</button>`;
        }).join('');

        const html = `
          <div style="min-width:220px;max-width:300px;font-family:system-ui">
            <div class="font-semibold text-sm mb-1">${p.cidade}</div>
            <div class="flex gap-3 text-xs text-gray-500 mb-2">
              <span>${p.total_uos} UO${p.total_uos > 1 ? 's' : ''}</span>
              <span>${p.total_contratos} contrato${p.total_contratos > 1 ? 's' : ''}</span>
              <span class="font-bold" style="color:${CALOR_MAP_COLORS[p.max_nivel as string]}">Score: ${p.avg_score}</span>
            </div>
            <div class="max-h-[120px] overflow-y-auto">${uoList}</div>
            <div class="mt-2 pt-1 border-t border-gray-200 space-y-0.5">
              <div class="text-xs text-gray-400 uppercase tracking-wider">Contratos</div>
              ${contractButtons}
            </div>
          </div>
        `;

        popupRef.current?.remove();
        const popup = new maplibregl.Popup({ closeOnClick: true, maxWidth: '320px' })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(m);

        // Attach drill-down listeners
        setTimeout(() => {
          popup.getElement()?.querySelectorAll('.risk-map-drill').forEach(btn => {
            btn.addEventListener('click', () => {
              const contractId = (btn as HTMLElement).dataset.contractId;
              if (contractId) navigate(`/gestao/inteligencia/${contractId}`);
            });
          });
        }, 50);

        popupRef.current = popup;
      });

      m.on('mouseenter', layerId, () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', layerId, () => { m.getCanvas().style.cursor = ''; });
    }

    // Fit bounds
    if (cities.length > 0) {
      const coords = cities.map(c => [c.lng, c.lat] as [number, number]);
      let [minLng, minLat, maxLng, maxLat] = [Infinity, Infinity, -Infinity, -Infinity];
      coords.forEach(([lng, lat]) => {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      });
      if (coords.length === 1) {
        m.flyTo({ center: coords[0], zoom: 10 });
      } else {
        m.fitBounds([minLng, minLat, maxLng, maxLat] as [number, number, number, number], { padding: 60, maxZoom: 12 });
      }
    }
  }, [mapReady, geojson, cities, data, onSelectUO, navigate]);

  // Fly to selected UO
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !mapReady || !selectedUOId) return;

    const row = data.find(d => d.uo_id === selectedUOId);
    if (row?.lng != null && row?.lat != null) {
      m.flyTo({ center: [row.lng, row.lat], zoom: 10, duration: 800 });
    }
  }, [selectedUOId, mapReady, data]);

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden border border-border">
      <div ref={containerRef} className="w-full h-full" />

      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-10">
        <div className="bg-card/90 backdrop-blur-xl border border-border/60 rounded-xl shadow-lg px-3 py-2.5 space-y-1.5">
          <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">
            Nível de Risco
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {(['critico', 'alto', 'moderado', 'baixo'] as const).map(nivel => {
              const labels: Record<string, string> = { critico: 'Crítico', alto: 'Alto', moderado: 'Moderado', baixo: 'Baixo' };
              const count = cities.filter(c => c.max_nivel === nivel).length;
              return (
                <div key={nivel} className="flex items-center gap-1.5 text-xs">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CALOR_MAP_COLORS[nivel] }} />
                  <span className="font-medium text-foreground">{labels[nivel]}</span>
                  <span className="text-muted-foreground">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskUOMap;
